import { Link } from '@inertiajs/react'
import type { ReactNode } from 'react'
import Layout from '@/components/Layout'
import { HeritageBadge, PrimaryButton } from '@/components/atoms'
import { BreadcrumbJsonLd } from '@/components/shared/json-ld'
import { CO_TAX_CREDIT } from '@/lib/constants'

type Plan = {
  name: string
  sub: string
  apr: string
  months: string
  note: string
  color: string
  href?: string
}

const plans: Plan[] = [
  {
    name: "CECU",
    sub: "Clean Energy Credit Union · e-bike loans",
    apr: "ask in store",
    months: "up to 60 mo",
    note: "The credit union that specializes in clean-energy purchases — they get cargo bikes. Membership is a one-time $5 join.",
    color: "#0FA0EA",
    href: "https://www.cleanenergycu.org/personal/electric-bicycle-loans/",
  },
  {
    name: "In-shop",
    sub: "Locals only · handshake friendly",
    apr: "Ask in store",
    months: "Ask in store",
    // FACT-CHECK: "5% / 6 mo / since 2014" specifics removed —
    // not verified with Ryan. Kept the handshake-plan framing but
    // pushed the actual terms to "Ask in store" so we never quote
    // a rate or term we haven't confirmed. Tracking:
    // docs/migration/RYAN-FACT-CHECK-ASK.md
    note: "We've done this for friends and neighbors for years. Ask in store for current terms.",
    color: "var(--accent)",
  },
]

function Financing() {
  return (
    <>
      <BreadcrumbJsonLd items={[{ name: "Financing", href: "/financing" }]} />

      {/* Hero */}
      <section className="bg-[var(--background-warm)]" style={{ padding: "72px 0 56px" }}>
        <div className="container" style={{ maxWidth: 900 }}>
          <HeritageBadge>Pay over time</HeritageBadge>
          {/*
            FACT-CHECK: previous h1 quoted "$7k bike, $185/mo" — a
            specific loan-math claim that depends on CECU rate + term +
            credit score, none of which we control. Replaced with a
            headline that points at CECU as the partner without quoting
            a specific monthly. Tracking:
            docs/migration/RYAN-FACT-CHECK-ASK.md
          */}
          <h1
            className="mt-6 text-[clamp(2.5rem,7.6vw,4.5rem)] md:text-[72px]"
            style={{ fontFamily: "var(--font-head)", lineHeight: 1.08 }}
          >
            Real loans for{" "}
            <span style={{ color: "var(--accent)" }}>cargo bikes.</span>
            <br />
            <span
              className="hand text-[clamp(2.75rem,8.4vw,5rem)] md:text-[80px]"
              style={{ color: "var(--primary)" }}
            >
              {"not credit-card APR."}
            </span>
          </h1>
          <p className="mt-6 text-lg" style={{ maxWidth: 620, color: "var(--muted-foreground)" }}>
            We point most folks to Clean Energy Credit Union — they do e-bike loans on real terms,
            not a credit-card APR. Stack with the ${CO_TAX_CREDIT} CO voucher and{" "}
            <Link href="/trade-in" className="underline text-accent">trade-in</Link>{" "}
            to bring the monthly even lower.{" "}
            <Link href="/tax-voucher" className="underline text-accent">Learn about the voucher →</Link>
          </p>
        </div>
      </section>

      {/* Plans section — ticket-stub variant */}
      <section className="bg-[var(--background)] py-[72px]" aria-labelledby="financing-plans-heading">
        <div className="container">
          <h2 id="financing-plans-heading" className="sr-only">Financing plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[22px]">
            {plans.map((p) => (
              <div
                key={p.name}
                className="bg-card border border-border rounded-[18px] p-[28px] shadow-[var(--shadow-sm)] relative h-full flex flex-col"
                style={{ overflow: "hidden" }}
              >
                {/* Ticket-stub dashed perforation line */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: "62%",
                    borderTop: "2px dashed var(--border-strong)",
                  }}
                />
                {/* Colored circle avatar */}
                <div
                  className="rounded-full mb-4 flex items-center justify-center font-bold text-background text-sm"
                  style={{ width: 56, height: 56, background: p.color, flexShrink: 0 }}
                >
                  {p.name.slice(0, 2)}
                </div>
                <h3 className="font-bold text-foreground" style={{ fontSize: 22 }}>{p.name}</h3>
                <p className="eyebrow text-accent mt-1">{p.sub}</p>
                <div className="flex gap-6 mt-4 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">APR</p>
                    <p className="font-semibold text-foreground">{p.apr}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Term</p>
                    <p className="font-semibold text-foreground">{p.months}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4 flex-1">{p.note}</p>
                {p.href && (
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-sm font-medium text-accent underline underline-offset-2 hover:text-primary transition-colors self-start"
                  >
                    See CECU&apos;s e-bike loans →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stack illustration — "The actual math" */}
      <section className="bg-[var(--background)] pb-[72px]" aria-labelledby="financing-math-heading">
        <div className="container">
          <div
            className="bg-secondary rounded-[var(--radius-lg)]"
            style={{ padding: "36px 44px" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-start">
              {/*
                FACT-CHECK: previous block quoted "$7,499 sticker →
                $185/mo" with "−$800 trade-in avg" and "÷60 mo CECU
                loan" — specific math that depends on a real Urban
                Arrow Family price, the average trade-in (unverified),
                and a CECU rate/term we don't quote. Replaced with a
                generic stacking diagram that names the three components
                (voucher + trade-in + CECU loan) without quoting the
                math. Tracking: docs/migration/RYAN-FACT-CHECK-ASK.md
              */}
              <div>
                <p className="eyebrow text-muted-foreground">How the stack works</p>
                <h2 id="financing-math-heading" className="mt-2 font-bold" style={{ fontSize: 28, fontFamily: "var(--font-head)" }}>
                  Sticker price →{" "}
                  <span className="hand" style={{ color: "var(--primary)" }}>monthly payment.</span>
                </h2>
                <div className="mt-6 flex flex-col sm:flex-row gap-6">
                  <div className="text-sm text-foreground">
                    <p>− ${CO_TAX_CREDIT} CO voucher</p>
                    <p>− trade-in credit (varies)</p>
                    <p>÷ CECU loan term</p>
                  </div>
                </div>
                <p className="mt-4 text-xs text-muted-foreground" style={{ maxWidth: 540 }}>
                  Actual monthly depends on the bike, your trade-in, and CECU&apos;s current rate and term. Ask in store for a worked example on the specific bike you&apos;re looking at.
                </p>
              </div>
              <div className="flex-shrink-0 pt-2">
                <Link href="/bikes">
                  <PrimaryButton>Pick your bike →</PrimaryButton>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

Financing.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default Financing
